import { FC, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Anchor,
  Box,
  Button,
  Center,
  Divider,
  MantineColorScheme,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";

import { fetchSsoProvider } from "@shared/api";
import { AUTH_PROVIDERS } from "@shared/constants";
import {
  useAuth,
  useLogEventMutation,
  useOrg,
  useTenant,
  useUrlSearch,
} from "@shared/hooks";
import { DEFAULT_TENANT_NAME } from "@shared/types";
import { getCLILoginToken, isAuthenticated, withToaster } from "@shared/utils";

import BrandSvg from "@assets/SemgrepLogo.svg?react";

export const SignInOptions: FC<{ colorScheme: MantineColorScheme }> = ({
  colorScheme: _colorScheme,
}) => {
  const [org] = useOrg();
  const { search } = useUrlSearch();
  const { tenantName } = useTenant();
  const history = useHistory();
  const showSso = search.has("sso");
  const { login } = useAuth();
  const { mutateAsync: logEvent } = useLogEventMutation();
  const [loggingIn, setLoggingIn] = useState(false);
  // get the CLI token on page load to store it in localstorage
  const cliToken = getCLILoginToken();

  const email = search.get("sso") || "";

  useEffect(() => {
    if (!cliToken || org) {
      return;
    }
    // Show initial notification to the user to sign in or sign up
    showNotification({
      title: "Welcome to Semgrep",
      message: "Have you created an account yet?",
      color: "teal",
      withCloseButton: true,
      withBorder: true,
      autoClose: 3000,
    });
    // Trigger persistent notification after 1 second
    setTimeout(() => {
      showNotification({
        title: "Semgrep CLI Login",
        message: "Sign in or sign up to continue activation!",
        color: "violet",
        withCloseButton: true,
        withBorder: true,
        autoClose: false,
      });
    }, 1000);
  }, [cliToken, org]);

  useEffect(() => {
    // Note that `AuthedRoute` will boot the user to `/login` (this page) if the token
    // is expired, and here we check that whether we have org data and that the user
    // is sufficiently authenticated before redirecting them to the dashboard.
    if (org && isAuthenticated()) {
      // User already logged in, redirect to dashboard
      history.push("/orgs/-");
    }
  }, [org, history]);

  const setEmail = (newEmail: string) => {
    const newSearch = new URLSearchParams(search.toString());
    newSearch.set("sso", newEmail);
    history.replace({ search: newSearch.toString() });
  };

  const setShowSso = (visible: boolean) => {
    const newSearch = new URLSearchParams(search.toString());
    if (visible) {
      newSearch.set("sso", "");
    } else {
      newSearch.delete("sso");
    }
    history.push({ search: newSearch.toString() });
  };

  const loginWithSSO = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const authProvider = await withToaster(
        fetchSsoProvider,
        (err) => `Could not find SSO provider for ${email}: ${err.message}`
      )(email);
      login(authProvider.provider_name, authProvider.provider_type, "/orgs/-");
    } catch (err) {
      console.error(err);
      setLoggingIn(false);
    }
  };

  return (
    <Box>
      {/* Content Box */}
      <Box maw={500} mx="auto">
        <Center>
          <BrandSvg width={150} />
        </Center>
        <Stack w={350} px={20} mx="auto" my="lg">
          {showSso ? (
            <>
              <Title order={2} ta="center">
                Single sign-on
              </Title>
              <form onSubmit={loginWithSSO}>
                <Stack>
                  <TextInput
                    size="md"
                    label="Email address"
                    placeholder="email@example.com"
                    type="email"
                    required
                    value={email}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setEmail(e.target.value);
                    }}
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus={true}
                    disabled={loggingIn}
                  />
                  <Button
                    size="md"
                    color="teal"
                    type="submit"
                    loading={loggingIn}
                  >
                    Continue to your provider
                  </Button>
                </Stack>
              </form>
              <Button
                variant="light"
                size="sm"
                color="gray"
                onClick={() => setShowSso(false)}
              >
                ← Back to sign in &nbsp;
              </Button>
            </>
          ) : (
            <>
              <Stack ta="center" fz="xl">
                <Title order={2}>Semgrep AppSec Platform</Title>
                <Divider
                  my="sm"
                  label="Sign in or sign up"
                  labelPosition="center"
                />
              </Stack>
              {tenantName === DEFAULT_TENANT_NAME &&
                AUTH_PROVIDERS.map((authProvider) => (
                  <Button
                    key={authProvider.provider_name}
                    leftSection={<FontAwesomeIcon icon={authProvider.icon} />}
                    size="md"
                    color={
                      authProvider.provider_name === "github"
                        ? "indigo"
                        : authProvider.provider_name === "gitlab"
                        ? "orange"
                        : undefined
                    }
                    onClick={async () => {
                      login(
                        authProvider.provider_name,
                        authProvider.provider_type,
                        "/orgs/-"
                      );
                      await logEvent({
                        event: "loginScreenLoginButton",
                        metadata: {
                          authProvider: authProvider.provider_name,
                        },
                      });
                    }}
                    data-testid={`${authProvider.display_name}SignIn`}
                  >
                    Sign in with {authProvider.display_name}
                  </Button>
                ))}
              <Button
                size="md"
                color="teal"
                variant="subtle"
                onClick={() => setShowSso(true)}
              >
                Use SSO
              </Button>
              <Divider my="0" />
              <Text size="xs" ta="center">
                By signing in you accept <br /> our{" "}
                <Anchor inherit c="teal" href="https://semgrep.dev/terms">
                  terms of service
                </Anchor>{" "}
                and{" "}
                <Anchor inherit c="teal" href="https://semgrep.dev/privacy">
                  privacy policy.
                </Anchor>
              </Text>
            </>
          )}
        </Stack>
      </Box>
    </Box>
  );
};
